'use client';

import { useMemo } from 'react';
import { useChromePathname } from '@/shared/components/providers/chrome-pathname';
import { IMMERSIVE_PATTERNS } from '@/shared/lib/chrome/route-classes';

/**
 * Detects whether the current route is "immersive" (e.g. video playback).
 * Immersive routes get a transparent header and collapsed sidebar.
 *
 * Reads the chrome-safe pathname (null during prerender and first client
 * render → false, the neutral shell) so shells stay instant-valid; the
 * pre-paint `data-immersive` stamp + CSS guards cover the hard-load paint
 * window.
 *
 * @param extraPatterns — additional RegExp patterns to treat as immersive,
 *   merged with the defaults. Define at module level to avoid re-renders.
 */
export function useImmersiveRoute(extraPatterns?: RegExp[]): boolean {
  const pathname = useChromePathname();

  return useMemo(() => {
    if (pathname === null) return false;
    const patterns = extraPatterns ? [...IMMERSIVE_PATTERNS, ...extraPatterns] : IMMERSIVE_PATTERNS;
    return patterns.some((p) => p.test(pathname));
  }, [pathname, extraPatterns]);
}
